import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { LanguageService } from '@shared/partner-ui/services/language.service';
import { SupportedLanguage, supportedLanguages } from '@shared/partner-ui/i18n/translations';
import { TranslatePipe } from '@shared/partner-ui/pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-language-menu',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './language-menu.component.html',
  styleUrl: './language-menu.component.scss'
})
export class LanguageMenuComponent {
  protected readonly options = supportedLanguages;
  protected readonly selectedLanguage$: Observable<SupportedLanguage>;
  protected isOpen = false;

  constructor(
    private readonly language: LanguageService,
    private readonly auth: AuthService,
    private readonly router: Router
  ) {
    this.selectedLanguage$ = this.language.languageChanges;
  }

  protected toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  protected selectLanguage(code: string, event: MouseEvent): void {
    event.stopPropagation();
    this.updateLanguage(code);
    this.isOpen = false;
  }

  @HostListener('document:click')
  protected closeMenu(): void {
    this.isOpen = false;
  }

  protected labelKey(code: string): string {
    if (code === 'fr') {
      return 'language.french';
    }
    if (code === 'nl') {
      return 'language.dutch';
    }
    return 'language.english';
  }

  protected trackByCode(_: number, code: string): string {
    return code;
  }

  protected get currentLanguage(): SupportedLanguage {
    return this.language.currentLanguage;
  }

  private updateLanguage(code: string): void {
    if (code === this.language.currentLanguage) {
      return;
    }
    if (this.options.includes(code as typeof supportedLanguages[number])) {
      const nextLanguage = code as typeof supportedLanguages[number];
      this.language.setLanguage(nextLanguage);
      this.navigateWithLanguage(nextLanguage);
      if (this.auth.isAuthenticated()) {
        void this.auth.updateLanguage(nextLanguage);
      }
    }
  }

  private navigateWithLanguage(languageCode: SupportedLanguage): void {
    const tree = this.router.parseUrl(this.router.url);
    const primary = tree.root.children['primary'];
    const segments = primary?.segments.map(segment => segment.path) ?? [];
    const hasLanguagePrefix = segments.length > 0 && this.language.isSupportedLanguage(segments[0]);
    const pathWithoutLanguage = hasLanguagePrefix ? segments.slice(1) : segments;

    void this.router.navigate(['/', languageCode, ...pathWithoutLanguage], {
      queryParams: tree.queryParams,
      fragment: tree.fragment ?? undefined
    });
  }
}
